import { darken, fade, lighten, mixColor, rgba, type Color } from "../../common/color.js";
import { insets, type Point, type Rect } from "../../common/geometry.js";
import { strokeTriangles } from "../art/markup/shapes.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import { drawCaptionGlyph, drawCheckmark } from "./glyphs.js";
import {
  Theme,
  type CaptionButton,
  type ScrollBarParts,
  type ThemeMetrics,
  type ThemePalette,
  type VisualState,
  type WindowVisualState,
} from "./Theme.js";

const UI_FONT = "'Segoe UI', system-ui, sans-serif";
const MONO_FONT = "'Cascadia Mono', Consolas, 'Courier New', monospace";

/**
 * A workable set of metrics to start from. Themes pass only what differs.
 */
export function makeMetrics(overrides: Partial<ThemeMetrics> = {}): ThemeMetrics {
  return {
    titleBarHeight: 32,
    windowBorder: 1,
    windowCornerRadius: 6,
    resizeGripSize: 6,
    captionButtonWidth: 44,
    windowPadding: insets(0),

    controlCornerRadius: 4,
    controlHeight: 30,
    buttonPadding: insets(14, 6),
    buttonMinWidth: 86,
    focusRingWidth: 2,
    focusRingInset: -2,

    textBoxPadding: insets(10, 5),

    checkBoxSize: 18,
    radioSize: 18,
    checkLabelGap: 8,

    splitterSize: 6,
    spinnerSize: 28,

    sliderThumbSize: 18,
    sliderTrackThickness: 4,
    spinButtonWidth: 17,

    scrollBarSize: 14,
    scrollBarMinThumb: 24,
    scrollBarArrows: false,

    listItemHeight: 26,
    listPadding: insets(3),
    headerHeight: 30,
    columnPadding: 8,

    dialogPadding: 16,
    dialogButtonGap: 8,

    font: { family: UI_FONT, size: 13 },
    captionFont: { family: UI_FONT, size: 13, weight: 600 },
    monoFont: { family: MONO_FONT, size: 12.5 },
    ...overrides,
  };
}

/**
 * The dozen colors a theme actually has opinions about. `derivePalette` fills
 * in the other thirty from these, which is what makes a new theme cheap to
 * write and keeps hover/pressed/disabled ramps consistent between them.
 */
export interface PaletteSeed {
  /** Behind every window. */
  desktop: Color;
  /** Window body. */
  surface: Color;
  /** Buttons and other raised chrome. */
  raised: Color;
  /** Text fields and list wells. */
  field: Color;
  /** Primary text on `surface`. */
  ink: Color;
  /** Default buttons, checks, selection, focus. */
  accent: Color;
  /** Text drawn on top of `accent`. */
  accentText: Color;
  /** Hairline borders. */
  border: Color;
  /** Active window's title bar. */
  titleBar: Color;
  /** Active window's title text. */
  titleText: Color;
  /** Caption on an ordinary button. Defaults to `ink`. */
  buttonText?: Color;
  /** Text typed into fields. Defaults to `ink`. */
  fieldText?: Color;
  /** Titles and headings. Defaults to `ink`. */
  headingText?: Color;
  /** Dark palettes gain contrast by lightening; light ones by darkening. */
  isDark: boolean;
  /** Applied last, for the handful of colors a theme wants to place by hand. */
  overrides?: Partial<ThemePalette>;
}

export function derivePalette(seed: PaletteSeed): ThemePalette {
  const { desktop, surface, raised, field, ink, accent, accentText, border, isDark } = seed;
  // "Toward the foreground" and "toward the background", whichever direction
  // that is for this palette.
  const em = (c: Color, a: number): Color => (isDark ? lighten(c, a) : darken(c, a));
  const de = (c: Color, a: number): Color => (isDark ? darken(c, a) : lighten(c, a));

  const palette: ThemePalette = {
    desktop,

    windowBg: surface,
    windowBorderActive: em(border, 0.2),
    windowBorderInactive: border,
    titleBarActive: seed.titleBar,
    titleBarInactive: mixColor(seed.titleBar, surface, 0.6),
    titleTextActive: seed.titleText,
    titleTextInactive: mixColor(seed.titleText, surface, 0.5),

    controlBg: raised,
    controlBgHover: em(raised, 0.1),
    controlBgPressed: em(raised, 0.18),
    controlBgDisabled: de(raised, 0.04),
    controlBorder: border,
    controlBorderHover: em(border, 0.18),

    fieldBg: field,
    fieldBgDisabled: mixColor(field, surface, 0.6),
    fieldBorder: border,
    fieldBorderFocused: accent,

    text: ink,
    textDisabled: mixColor(ink, surface, 0.55),
    textSubtle: mixColor(ink, surface, 0.3),
    buttonText: seed.buttonText ?? ink,
    fieldText: seed.fieldText ?? ink,
    placeholderText: mixColor(ink, surface, 0.3), // settled below, after any overrides
    headingText: seed.headingText ?? ink,

    accent,
    accentHover: lighten(accent, 0.12),
    accentPressed: darken(accent, 0.15),
    accentText,

    selectionBg: accent,
    selectionText: accentText,
    selectionInactiveBg: mixColor(accent, surface, 0.65),
    textSelectionBg: fade(accent, 0.45),

    scrollTrack: rgba(0, 0, 0, 0),
    scrollThumb: fade(ink, 0.28),
    scrollThumbHover: fade(ink, 0.45),
    scrollThumbPressed: fade(ink, 0.6),

    headerBg: de(surface, 0.03),
    headerBgHover: em(surface, 0.05),
    headerText: mixColor(ink, surface, 0.15),
    gridLine: fade(border, 0.75),

    focusRing: accent,
    shadow: rgba(0, 0, 0, isDark ? 0.55 : 0.25),

    bevelHighlight: em(raised, 0.28),
    bevelLight: em(raised, 0.12),
    bevelShadow: de(raised, 0.18),
    bevelDarkShadow: de(raised, 0.34),
  };

  const result = { ...palette, ...seed.overrides };
  // A watermark is muted text until a theme says otherwise, so one that pins only its muted
  // text keeps them the same color - which is what every field drew before it had its own.
  if (seed.overrides?.placeholderText === undefined) result.placeholderText = result.textSubtle;
  return result;
}

export interface BaseThemeOptions {
  name: string;
  isDark: boolean;
  palette: ThemePalette;
  metrics?: Partial<ThemeMetrics>;
}

/**
 * A complete, flat, bordered theme driven entirely by its palette and metrics.
 *
 * Every new theme in Webwin extends this and overrides only what makes it
 * distinctive — an accent rail, a glow, a gold inner line. ModernTheme and
 * ClassicTheme deliberately do not: they are faithful reproductions of specific
 * Windows looks and are hand-written end to end.
 */
export class BaseTheme extends Theme {
  readonly name: string;
  readonly isDark: boolean;
  readonly palette: ThemePalette;
  readonly metrics: ThemeMetrics;

  constructor(options: BaseThemeOptions) {
    super();
    this.name = options.name;
    this.isDark = options.isDark;
    this.palette = options.palette;
    this.metrics = makeMetrics(options.metrics);
  }

  // --- hooks subclasses reach for ---------------------------------------

  /** Radius used for the window body. */
  protected get frameRadius(): number {
    return this.metrics.windowCornerRadius;
  }

  /** Radius used for buttons, fields and list wells. */
  protected get controlRadius(): number {
    return this.metrics.controlCornerRadius;
  }

  /** Drawn after the window body and title bar, before the caption buttons. */
  protected decorateWindow(_rc: RenderContext, _bounds: Rect, _s: WindowVisualState): void {}

  /** Drawn after a button's fill and border. */
  protected decorateButton(_rc: RenderContext, _r: Rect, _s: VisualState): void {}

  /** Fill color for a button in the given state. */
  protected buttonFill(s: VisualState): Color {
    const p = this.palette;
    const accent = s.isDefault === true;
    if (s.disabled) return accent ? fade(p.accent, 0.4) : p.controlBgDisabled;
    if (s.pressed) return accent ? p.accentPressed : p.controlBgPressed;
    if (s.hovered) return accent ? p.accentHover : p.controlBgHover;
    return accent ? p.accent : p.controlBg;
  }

  protected buttonBorder(s: VisualState): Color {
    const p = this.palette;
    if (s.isDefault === true) return fade(p.accent, 0.65);
    return s.hovered ? p.controlBorderHover : p.controlBorder;
  }

  /** Modern-style themes underline a focused field with the accent color. */
  protected get focusUnderline(): boolean {
    return false;
  }

  // --- window chrome ----------------------------------------------------

  override windowClientRect(bounds: Rect, s: WindowVisualState): Rect {
    const b = this.frameBorder(s);
    const top = b + this.captionHeight(s);
    return {
      x: b,
      y: top,
      width: Math.max(0, bounds.width - b * 2),
      height: Math.max(0, bounds.height - top - b),
    };
  }

  override captionButtonRect(
    bounds: Rect,
    button: CaptionButton,
    s: WindowVisualState,
  ): Rect | null {
    const b = this.frameBorder(s);
    const h = this.captionHeight(s);
    const w = this.metrics.captionButtonWidth;
    const order: CaptionButton[] = [];
    if (s.showMinimize) order.push("minimize");
    if (s.showMaximize) order.push("maximize");
    if (s.showClose) order.push("close");
    const index = order.indexOf(button);
    if (index < 0) return null;
    const fromRight = order.length - 1 - index;
    return { x: bounds.width - b - (fromRight + 1) * w, y: b, width: w, height: h };
  }

  override drawWindowFrame(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const p = this.palette;
    const border = this.frameBorder(s);
    const captionHeight = this.captionHeight(s);
    const radius = s.maximized ? 0 : this.frameRadius;
    const r: Rect = { x: 0, y: 0, width: bounds.width, height: bounds.height };

    if (s.active && !s.maximized && p.shadow.a > 0) {
      rc.fillRect({ x: 2, y: 4, width: r.width - 4, height: r.height - 2 }, fade(p.shadow, 0.5), radius);
      rc.fillRect({ x: 5, y: 8, width: r.width - 10, height: r.height - 6 }, fade(p.shadow, 0.4), radius);
    }

    rc.drawRect(r, {
      fill: p.windowBg,
      radius,
      borderWidth: border,
      borderColor: s.active ? p.windowBorderActive : p.windowBorderInactive,
    });

    const b = border;
    const bar: Rect = { x: b, y: b, width: r.width - b * 2, height: captionHeight };
    const barFill = s.active ? p.titleBarActive : p.titleBarInactive;
    rc.fillRect(bar, barFill, radius);
    // Square off the bottom edge so the bar meets the client area flush.
    rc.fillRect(
      { x: bar.x, y: bar.y + radius, width: bar.width, height: bar.height - radius },
      barFill,
    );

    this.decorateWindow(rc, bounds, s);
    this.drawCaptionButtons(rc, bounds, s);
  }

  protected drawCaptionButtons(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const p = this.palette;
    for (const button of ["minimize", "maximize", "close"] as CaptionButton[]) {
      const r = this.captionButtonRect(bounds, button, s);
      if (!r) continue;
      const hovered = s.hoveredButton === button;
      const pressed = s.pressedButton === button;
      if (hovered || pressed) {
        rc.fillRect(r, this.captionButtonHighlight(button, pressed));
      }
      const ink =
        button === "close" && (hovered || pressed)
          ? this.closeGlyphInk()
          : s.active
            ? p.titleTextActive
            : p.titleTextInactive;
      drawCaptionGlyph(rc, r, button, ink, s.maximized, s.active ? p.titleBarActive : p.titleBarInactive);
    }
  }

  protected captionButtonHighlight(button: CaptionButton, pressed: boolean): Color {
    const p = this.palette;
    if (button === "close") return pressed ? darken(p.accent, 0.2) : p.accent;
    return pressed ? p.controlBgPressed : p.controlBgHover;
  }

  protected closeGlyphInk(): Color {
    return this.palette.accentText;
  }

  // --- controls ---------------------------------------------------------

  override drawButton(rc: RenderContext, r: Rect, s: VisualState): void {
    rc.drawRect(r, {
      fill: this.buttonFill(s),
      radius: this.controlRadius,
      borderWidth: 1,
      borderColor: this.buttonBorder(s),
    });
    this.decorateButton(rc, r, s);
    if (s.focused && !s.disabled) this.drawFocusRing(rc, r);
  }

  override drawShapedChrome(
    rc: RenderContext,
    rings: readonly (readonly Point[])[],
    s: VisualState,
    kind: "button" | "panel",
    fill?: Color | null,
  ): void {
    super.drawShapedChrome(rc, rings, s, kind, fill ?? (kind === "button" ? this.buttonFill(s) : null));
    if (kind === "button" && !s.disabled) {
      for (const ring of rings) rc.fillTriangles(strokeTriangles(ring, true, 1), this.buttonBorder(s));
    }
  }

  override buttonTextColor(s: VisualState): Color {
    const p = this.palette;
    if (s.disabled) return p.textDisabled;
    return s.isDefault === true ? p.accentText : p.buttonText;
  }

  override drawTextBox(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: s.disabled ? p.fieldBgDisabled : p.fieldBg,
      radius: this.controlRadius,
      borderWidth: 1,
      borderColor: s.disabled
        ? p.controlBorder
        : s.focused
          ? p.fieldBorderFocused
          : s.hovered
            ? p.controlBorderHover
            : p.fieldBorder,
    });
    if (this.focusUnderline && s.focused && !s.disabled) {
      rc.fillRect(
        { x: r.x + 1, y: r.y + r.height - 2, width: r.width - 2, height: 2 },
        p.fieldBorderFocused,
      );
    }
  }

  override drawCheckBox(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    const on = s.checked === true || s.indeterminate === true;
    let fill: Color;
    if (s.disabled) fill = on ? fade(p.accent, 0.4) : p.controlBgDisabled;
    else if (on) fill = s.pressed ? p.accentPressed : s.hovered ? p.accentHover : p.accent;
    else fill = s.pressed ? p.controlBgPressed : s.hovered ? p.controlBgHover : p.fieldBg;

    rc.drawRect(r, {
      fill,
      radius: Math.min(3, this.controlRadius),
      borderWidth: 1,
      borderColor: on ? fill : s.hovered ? p.controlBorderHover : p.controlBorder,
    });

    const ink = s.disabled ? p.textDisabled : p.accentText;
    if (s.indeterminate) {
      rc.fillRect(
        { x: r.x + r.width * 0.25, y: r.y + r.height / 2 - 1, width: r.width * 0.5, height: 2 },
        ink,
      );
    } else if (s.checked) {
      drawCheckmark(rc, r, ink);
    }
  }

  override drawRadioButton(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    const radius = Math.min(r.width, r.height) / 2;
    const on = s.checked === true;
    const fill = s.disabled
      ? p.controlBgDisabled
      : s.pressed
        ? p.controlBgPressed
        : s.hovered
          ? p.controlBgHover
          : p.fieldBg;
    const border = s.disabled
      ? p.controlBorder
      : on
        ? s.hovered
          ? p.accentHover
          : p.accent
        : s.hovered
          ? p.controlBorderHover
          : p.controlBorder;
    rc.drawRect(r, { fill, radius, borderWidth: 1, borderColor: border });
    if (on) {
      const dot = Math.max(4, Math.round(r.width * 0.44));
      const inset = (r.width - dot) / 2;
      rc.fillRect(
        { x: r.x + inset, y: r.y + inset, width: dot, height: dot },
        s.disabled ? p.textDisabled : p.accent,
        dot / 2,
      );
    }
  }

  override drawPanel(rc: RenderContext, r: Rect, sunken: boolean): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: sunken ? p.fieldBg : p.windowBg,
      radius: this.controlRadius,
      borderWidth: 1,
      borderColor: p.controlBorder,
    });
  }

  override drawScrollBar(rc: RenderContext, parts: ScrollBarParts, s: VisualState): void {
    const p = this.palette;
    if (p.scrollTrack.a > 0) rc.fillRect(parts.bounds, p.scrollTrack);
    if (parts.thumb.width <= 0 || parts.thumb.height <= 0) return;

    const hovered = parts.hoveredPart === "thumb" || s.hovered;
    const pressed = parts.pressedPart === "thumb";
    const color = pressed ? p.scrollThumbPressed : hovered ? p.scrollThumbHover : p.scrollThumb;

    const slim = hovered || pressed ? 0 : 3;
    const t = parts.thumb;
    const r: Rect = parts.vertical
      ? { x: t.x + slim, y: t.y, width: Math.max(2, t.width - slim * 2), height: t.height }
      : { x: t.x, y: t.y + slim, width: t.width, height: Math.max(2, t.height - slim * 2) };
    rc.fillRect(r, color, Math.min(r.width, r.height) / 2);
  }

  override drawListBackground(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: s.disabled ? p.fieldBgDisabled : p.fieldBg,
      radius: this.controlRadius,
      borderWidth: 1,
      borderColor: s.focused ? p.fieldBorderFocused : p.fieldBorder,
    });
  }

  override drawListItem(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    if (s.selected) {
      rc.fillRect(r, s.active === false ? p.selectionInactiveBg : p.selectionBg, Math.min(3, this.controlRadius));
    } else if (s.hovered) {
      rc.fillRect(r, p.controlBgHover, Math.min(3, this.controlRadius));
    }
  }

  override drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.fillRect(r, s.pressed ? p.controlBgPressed : s.hovered ? p.headerBgHover : p.headerBg);
    rc.vLine(r.x + r.width - 1, r.y + 5, r.height - 10, p.gridLine);
    rc.hLine(r.x, r.y + r.height - 1, r.width, p.gridLine);
  }

  override drawFocusRing(rc: RenderContext, r: Rect): void {
    const m = this.metrics;
    const i = m.focusRingInset;
    rc.strokeRect(
      { x: r.x + i, y: r.y + i, width: r.width - i * 2, height: r.height - i * 2 },
      this.palette.focusRing,
      m.focusRingWidth,
      this.controlRadius + Math.abs(i),
    );
  }
}

/** Convenience for themes that only vary a font family. */
export function fontStack(...families: string[]): string {
  return families.join(", ");
}
